import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Car, CheckCircle2, Loader2, RefreshCw, Route, AlertTriangle, FileText } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { adminGet, adminPut, reportError } from './adminApi';
import { AccTrips } from '../easer/AccTrips';
import { Map as MapIcon, BellRing } from 'lucide-react';

const fmtKm = (km) => `${Number(km || 0).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const monthValue = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const LINE_STATUS = {
  ok: { label: 'Ligne PAFK sur le bon', cls: 'bg-[hsl(var(--brand-mint))] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30' },
  partial: { label: 'Km dans le libellé (service à relier)', cls: 'bg-[hsl(var(--brand-apricot))]/20 text-foreground border-[hsl(var(--brand-apricot))]' },
  error: { label: 'Ligne non créée', cls: 'bg-destructive/10 text-destructive border-destructive/30' },
};

/** Kilomètres d'accompagnement (bouton ACC des Easers) : liste à facturer, case « facturé », totaux du mois. */
export function AdminAccompagnementsTab({ adminKey }) {
  const [items, setItems] = useState(null);
  const [totals, setTotals] = useState(null);
  const [filter, setFilter] = useState('to_bill'); // to_bill | billed | all
  const [month, setMonth] = useState(monthValue());
  const [busyId, setBusyId] = useState(null);
  const [active, setActive] = useState([]);
  const [remindAfter, setRemindAfter] = useState(120);
  const [trip, setTrip] = useState(null); // {loading, data} : trac\u00e9 GPS ouvert dans une fen\u00eatre

  const openTrip = async (item) => {
    setTrip({ loading: true, data: null, item });
    try {
      const r = await adminGet(`/api/admin/accompagnements/${item.id}`, adminKey);
      setTrip({ loading: false, data: r.data, item });
    } catch (err) {
      reportError('trajet', err, toast.error);
      setTrip(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const params = { key: adminKey };
      if (filter === 'to_bill') params.billed = 'false';
      if (filter === 'billed') params.billed = 'true';
      if (month) params.month = month;
      const r = await adminGet('/api/admin/accompagnements', adminKey, params);
      setItems(r.data.items || []);
      setTotals(r.data.totals || null);
      setActive(r.data.active || []);
      if (r.data.remind_after_min) setRemindAfter(r.data.remind_after_min);
    } catch (err) {
      reportError('accompagnements', err, toast.error);
      setItems((i) => i || []);
    }
  }, [adminKey, filter, month]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (item, billed) => {
    setBusyId(item.id);
    try {
      const r = await adminPut(`/api/admin/accompagnements/${item.id}/billed`, adminKey, {}, { billed, note: '' });
      setItems((list) => (list || []).map((x) => (x.id === item.id ? { ...x, ...r.data } : x)));
      toast.success(billed ? `${fmtKm(item.km_billed)} marqués facturés` : 'Remis « à facturer »');
      if (filter !== 'all') setTimeout(load, 400);
    } catch (err) {
      reportError('accompagnement facturé', err, toast.error);
    } finally {
      setBusyId(null);
    }
  };

  const byClient = useMemo(() => {
    const map = new Map();
    for (const i of items || []) {
      const k = i.client_name || 'Client inconnu';
      map.set(k, (map.get(k) || 0) + Number(i.km_billed || 0));
    }
    return [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [items]);

  return (
    <div className="space-y-5" data-testid="admin-acc-tab">
      <Card className="p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-['Space_Grotesk'] text-xl font-semibold flex items-center gap-2"><Car className="h-5 w-5 text-[hsl(var(--accent))]" /> Accompagnements — kilomètres à facturer</h2>
            <p className="text-sm text-muted-foreground">Trajets GPS lancés avec le bouton « ACC » pendant une prestation. Chaque fin d'accompagnement ajoute une ligne PAFK (participation aux frais kilométriques) sur le bon d'intervention ; cochez « facturé » une fois la facture émise.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="admin-acc-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Actualiser</Button>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList>
              <TabsTrigger value="to_bill" data-testid="admin-acc-filter-to-bill">À facturer</TabsTrigger>
              <TabsTrigger value="billed" data-testid="admin-acc-filter-billed">Facturés</TabsTrigger>
              <TabsTrigger value="all" data-testid="admin-acc-filter-all">Tous</TabsTrigger>
            </TabsList>
          </Tabs>
          <div>
            <Label className="text-xs" htmlFor="acc-month">Mois</Label>
            <Input id="acc-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-44" data-testid="admin-acc-month" />
          </div>
          {month && <Button variant="ghost" size="sm" onClick={() => setMonth('')} data-testid="admin-acc-month-clear">Toutes les dates</Button>}
        </div>
        {totals && (
          <div className="grid sm:grid-cols-3 gap-3" data-testid="admin-acc-totals">
            <div className="rounded-lg bg-secondary/60 p-3"><p className="text-xs text-muted-foreground">Accompagnements</p><p className="text-xl font-semibold">{totals.count}</p></div>
            <div className="rounded-lg bg-[hsl(var(--brand-apricot))]/20 p-3"><p className="text-xs text-muted-foreground">Km à facturer</p><p className="text-xl font-semibold" data-testid="admin-acc-km-to-bill">{fmtKm(totals.km_to_bill)}</p></div>
            <div className="rounded-lg bg-secondary/60 p-3"><p className="text-xs text-muted-foreground">Km total (sélection)</p><p className="text-xl font-semibold">{fmtKm(totals.km)}</p></div>
          </div>
        )}
      </Card>

      {active.length > 0 && (
        <Card className="p-5 space-y-2" data-testid="admin-acc-active">
          <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2"><Car className="h-4 w-4 text-destructive" /> En cours en ce moment</h3>
          <ul className="divide-y divide-border rounded-lg border border-border text-sm">
            {active.map((a) => {
              const h = Math.floor(a.elapsed_min / 60); const m = a.elapsed_min % 60;
              return (
                <li key={a.id} className="px-3 py-2 flex flex-wrap items-center justify-between gap-2" data-testid={`admin-acc-active-${a.id}`}>
                  <span><span className="font-medium">{a.user_name}</span> · {a.client_name || a.label} · depuis {fmtDate(a.started_at).slice(-5)} ({h ? `${h} h ${String(m).padStart(2, '0')}` : `${m} min`}) · {fmtKm(a.km_measured)}</span>
                  <span className="flex items-center gap-2">
                    {a.overdue && <Badge className="border bg-destructive/10 text-destructive border-destructive/30" data-testid={`admin-acc-overdue-${a.id}`}><BellRing className="h-3 w-3 mr-1" /> Plus de {Math.round(remindAfter / 60)} h{a.reminded_at ? ' · Easer rappelé' : ''}</Badge>}
                    <Button variant="outline" size="sm" className="h-8" onClick={() => openTrip(a)} data-testid={`admin-acc-map-${a.id}`}><MapIcon className="h-3.5 w-3.5 mr-1" /> Carte</Button>
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="text-xs text-muted-foreground">Au-delà de {Math.round(remindAfter / 60)} h, l'Easer reçoit une notification de rappel toutes les heures ; aucun arrêt automatique.</p>
        </Card>
      )}

      <Card className="p-5">
        {items === null ? <Skeleton className="h-40 w-full" /> : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center" data-testid="admin-acc-empty">Aucun accompagnement {filter === 'to_bill' ? 'à facturer' : ''} sur cette période.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Client</th><th className="py-2 pr-3">Easer</th><th className="py-2 pr-3">Km</th><th className="py-2 pr-3">Bon d'intervention</th><th className="py-2 pr-3">Remarque</th><th className="py-2 pr-3">Trajet</th><th className="py-2 pr-3">Facturé</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => {
                  const st = LINE_STATUS[i.line_status] || null;
                  return (
                    <tr key={i.id} className="border-b border-border/60 align-top" data-testid={`admin-acc-row-${i.id}`}>
                      <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(i.started_at)}<span className="block text-[11px] text-muted-foreground">{Math.max(1, Math.round((i.duration_s || 0) / 60))} min</span></td>
                      <td className="py-2 pr-3 font-medium">{i.client_name || '—'}</td>
                      <td className="py-2 pr-3">{i.user_name}</td>
                      <td className="py-2 pr-3 whitespace-nowrap"><span className="font-semibold" data-testid={`admin-acc-km-${i.id}`}>{fmtKm(i.km_billed)}</span>{Math.abs((i.km_measured || 0) - (i.km_billed || 0)) >= 0.3 && <span className="block text-[11px] text-muted-foreground flex items-center gap-1"><Route className="h-3 w-3" /> GPS {fmtKm(i.km_measured)}</span>}</td>
                      <td className="py-2 pr-3"><span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5 text-muted-foreground" /> {i.fichinter_ref || '—'}</span>{st && <Badge className={`mt-1 border text-[10px] ${st.cls}`}>{i.line_status === 'error' && <AlertTriangle className="h-3 w-3 mr-1" />}{st.label}</Badge>}</td>
                      <td className="py-2 pr-3 text-muted-foreground max-w-[220px]">{i.comment || ''}</td>
                      <td className="py-2 pr-3"><Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => openTrip(i)} title="Voir le trajet GPS" data-testid={`admin-acc-map-${i.id}`}><MapIcon className="h-4 w-4" /></Button></td>
                      <td className="py-2 pr-3">
                        <label className="flex items-center gap-2 text-xs whitespace-nowrap">
                          {busyId === i.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Checkbox checked={!!i.billed} onCheckedChange={(v) => toggle(i, !!v)} data-testid={`admin-acc-billed-${i.id}`} />}
                          {i.billed ? <span className="text-[hsl(var(--primary))] flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {fmtDate(i.billed_at)}</span> : 'À facturer'}
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {byClient.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-acc-by-client">
            {byClient.map(([name, km]) => <Badge key={name} variant="outline" className="text-xs">{name} · {fmtKm(km)}</Badge>)}
          </div>
        )}
      </Card>

      <Dialog open={!!trip} onOpenChange={(o) => { if (!o) setTrip(null); }}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="admin-acc-trip-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MapIcon className="h-5 w-5 text-[hsl(var(--accent))]" /> Trajet de l'accompagnement</DialogTitle>
            <DialogDescription>{trip?.item?.client_name || trip?.item?.label} · {trip?.item?.user_name} · {fmtDate(trip?.item?.started_at)}{trip?.item?.fichinter_ref ? ` · ${trip.item.fichinter_ref}` : ''}</DialogDescription>
          </DialogHeader>
          {trip?.loading ? <Skeleton className="h-72 w-full" /> : trip?.data ? (
            trip.data.status === 'active' ? <AccTrips sessions={[]} active={trip.data} height={360} /> : <AccTrips sessions={[trip.data]} height={360} />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
